import { isNil } from '@pct/ts-typeguard';
import type uPlot from 'uplot';

// Típus a színátmenet megadásához: pl. [0, "red"] vagy [-Infinity, "blue"]
export type ScaleStop = [number, string];

// Per-uPlot példányhoz kötött cache, hogy ne maradjon benne idegen canvas-gradiens
const gradientCacheByInstance = new WeakMap<
  uPlot,
  Map<string, { min: number; max: number; gradient: string | CanvasGradient }>
>();

/**
 * uPlot skálához igazított lineáris színátmenetet (Gradient) generál.
 * * @param u Az uPlot példány
 * @param scaleKey A skála neve (pl. 'ms' vagy 'x')
 * @param scaleStops A határértékek és a hozzájuk tartozó színek tömbje
 * @param cacheKey A gyorsítótárazáshoz azonosító. Ha a görbe több eleme u.a. a színt venné fel (pl. vonal és pont), ne számolja újra
 * @param isVertical Orientáció: true = Függőleges (Y) false = Vízszintes (X)
 */
export function scaleGradient(
  u: uPlot,
  scaleKey: string,
  scaleStops: ScaleStop[],
  cacheKey?: string,
  isVertical = true,
): string | CanvasGradient {
  const scale = u.scales[scaleKey];

  if (isNil(scale) || isNil(scale.min) || isNil(scale.max)) {
    return scaleStops[1][1]; // Védelmi fallback (a threshold feletti szín kell LF variability esetén)
  }

  // Ha ugyanaz a cacheKey és a skála sem mozdult el az előző hívás óta,
  // akkor azonnal visszaadjuk a korábban kiszámolt gradientet
  const gradientCache = getCache(u);

  if (cacheKey !== undefined) {
    const cached = gradientCache.get(cacheKey);
    if (cached?.min === scale.min && cached.max === scale.max) {
      return cached.gradient;
    }
  }

  // Megkeressük a látható tartományba eső stop-okat
  let minStopIdx = 0;
  let maxStopIdx = 0;

  for (let i = 0; i < scaleStops.length; i++) {
    const stopVal = scaleStops[i][0];

    if (stopVal <= scale.min || minStopIdx === null) {
      minStopIdx = i;
    }

    maxStopIdx = i;

    if (stopVal >= scale.max) {
      break;
    }
  }

  // Ha a teljes látható tartomány egyetlen zónába esik, sima színt adunk vissza
  if (minStopIdx === maxStopIdx) {
    return scaleStops[minStopIdx][1];
  }

  let minStopVal = scaleStops[minStopIdx][0];
  let maxStopVal = scaleStops[maxStopIdx][0];

  if (minStopVal === -Infinity) {
    minStopVal = scale.min;
  }

  if (maxStopVal === Infinity) {
    maxStopVal = scale.max;
  }

  // Értékek átváltása pixel pozíciókká a vásznon
  const minStopPos = u.valToPos(minStopVal, scaleKey, true);
  const maxStopPos = u.valToPos(maxStopVal, scaleKey, true);
  const range = minStopPos - maxStopPos;

  let x0 = 0,
    y0 = 0,
    x1 = 0,
    y1 = 0;

  if (isVertical) {
    // Függőleges (Y tengely)
    y0 = minStopPos;
    y1 = maxStopPos;
  } else {
    // Vízszintes (X tengely)
    x0 = minStopPos;
    x1 = maxStopPos;
  }

  // Gradient létrehozása az uPlot beépített canvas kontextusával
  const ctx = u.ctx;
  const grd = ctx.createLinearGradient(x0, y0, x1, y1);
  let prevColor = '';

  for (let i = minStopIdx; i <= maxStopIdx; i++) {
    const s = scaleStops[i];

    let stopPos;
    if (i === minStopIdx) {
      stopPos = minStopPos;
    } else if (i === maxStopIdx) {
      stopPos = maxStopPos;
    } else {
      stopPos = u.valToPos(s[0], scaleKey, true);
    }

    const pct = (minStopPos - stopPos) / range;

    if (i > minStopIdx) {
      grd.addColorStop(pct, prevColor);
    }

    prevColor = s[1];
    grd.addColorStop(pct, prevColor);
  }

  if (cacheKey !== undefined) {
    gradientCache.set(cacheKey, {
      min: scale.min,
      max: scale.max,
      gradient: grd, // ahol a 'grd' a kiszámított CanvasGradient
    });
  }

  return grd;
}

function getCache(u: uPlot): Map<string, { min: number; max: number; gradient: string | CanvasGradient }> {
  let c = gradientCacheByInstance.get(u);
  if (c === undefined) {
    c = new Map();
    gradientCacheByInstance.set(u, c);
  }
  return c;
}
